<script setup lang="ts">
    import { richTextResolver } from '@storyblok/richtext';

    import type { NewsOverviewStoryblok } from '~storyblok-types/NewsOverviewStoryblok';
    import type { PageNewsStoryblok } from '~storyblok-types/PageNewsStoryblok';
    import type { PageNewsReferenceStoryblok } from '~storyblok-types/PageNewsReferenceStoryblok';
    import type { StoryblokMultilink } from '~storyblok-types/storyblok';

    const props = defineProps<{ blok: NewsOverviewStoryblok }>();
    const { lang } = useLanguage();
    const { t } = useI18n();
    const { data: newsData } = await useStories<PageNewsStoryblok | PageNewsReferenceStoryblok>({
        resolve_relations: 'PageNewsReference.reference',
        filter_query: {
            component: {
                in: 'PageNews,PageNewsReference'
            }
        }
    });

    const { getReferenceUuid, normalizeNewsCardStory } = useResolvedNewsStory();

    const referencedNewsUuids = computed(() => {
        return [
            ...new Set(
                (newsData.value ?? []).map((story) => getReferenceUuid(story)).filter((uuid): uuid is string => !!uuid)
            )
        ];
    });

    const referencedNewsParams = computed(() => ({
        ...(referencedNewsUuids.value.length ? { by_uuids: referencedNewsUuids.value.join(',') } : {}),
        filter_query: {
            component: {
                in: 'PageNews'
            }
        }
    }));

    const referencedFetchOptions = computed(() => ({
        disabled: referencedNewsUuids.value.length === 0
    }));

    const { data: referencedNewsData } = await useStories<PageNewsStoryblok>(
        referencedNewsParams,
        referencedFetchOptions
    );

    const { data: datasourceData } = await useDatasourceEntries('news-categories');
    const datasourceEntries = computed(() => datasourceData.value || []);

    const processedNewsArticles = computed(() => {
        const resolveCategoryKey = (category: string | number) => {
            const matchedCategory = datasourceEntries.value.find((data) => data.value === category);
            return matchedCategory ? matchedCategory.dimension_value || matchedCategory.value : category;
        };

        const getUrlProperty = (linkedStory: StoryblokMultilink | undefined): string => {
            if (
                linkedStory &&
                'story' in linkedStory &&
                linkedStory.story &&
                'url' in linkedStory.story &&
                linkedStory.story.url
            ) {
                return linkedStory.story.url;
            }
            return '';
        };

        const rootUrl = getUrlProperty(props.blok.newsRoot);
        const newsArticles =
            newsData.value
                ?.filter((story) => story.full_slug.startsWith(`${lang.value}/${rootUrl}`))
                .map((story) => normalizeNewsCardStory(story, referencedNewsData.value ?? []))
                .filter((story): story is NonNullable<typeof story> => Boolean(story))
                .sort(
                    (a, b) =>
                        new Date(b.content.Date || b.published_at || b.created_at).getTime() -
                        new Date(a.content.Date || a.published_at || a.created_at).getTime()
                ) || [];

        let list = newsArticles;

        if (props.blok.category) {
            const translatedCategory = resolveCategoryKey(props.blok.category);
            list = newsArticles.filter((news) =>
                (news.content?.Categories ?? []).some(
                    (category: string | number) => resolveCategoryKey(category) === translatedCategory
                )
            );
        }

        return list.map((news) => ({
            ...news,
            parsedAbstract: richTextResolver().render(news.content?.Abstract ?? EMPTY_RICHTEXT)
        }));
    });
</script>

<template>
    <div
        class="news-wrapper"
        :class="props.blok.singleRow ? '--single-row' : ''"
    >
        <NewsCard
            v-for="news in processedNewsArticles.slice(0, parseInt(props.blok.amount || '4'))"
            :key="news.uuid"
            :news="news"
            :categories="datasourceEntries"
        />

        <div
            v-if="processedNewsArticles.length == 0"
            class="no-news-container"
        >
            <img
                src="../../assets/images/404.svg"
                :alt="t('general.error')"
            />
            <h2>{{ t('pageNews.error.header') }}</h2>
            <p>
                {{ t('pageNews.error.text') }}
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/news/news-overview.scss';
    @import '~/assets/scss/pages/page-news-overview.scss';
</style>
